'use client';

import { RefObject, useEffect, useLayoutEffect, useRef } from 'react';

const useIsoLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

function findScope(el: Element): Element | null {
  let node = el.closest('[data-equalize-root]')?.parentElement ?? null;
  while (node) {
    if (node.querySelectorAll('[data-equalize-root]').length >= 2) return node;
    node = node.parentElement;
  }
  return null;
}

function equalize(scope: Element, group: string) {
  const els = [
    ...scope.querySelectorAll<HTMLElement>(`[data-equalize-group="${group}"]`),
  ];
  els.forEach(el => (el.style.minHeight = ''));
  if (els.length < 2) return;
  const max = Math.max(...els.map(el => el.getBoundingClientRect().height));
  els.forEach(el => (el.style.minHeight = `${max}px`));
}

export function useEqualHeight<T extends HTMLElement>(
  group: string,
  enabled = true
): RefObject<T | null> {
  const ref = useRef<T>(null);
  useIsoLayoutEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return;
    const scope = findScope(el);
    if (!scope) return;

    el.dataset.equalizeGroup = group;
    const run = () => equalize(scope, group);
    run();

    let width = scope.clientWidth;
    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            if (scope.clientWidth === width) return;
            width = scope.clientWidth;
            run();
          })
        : null;
    observer?.observe(scope);

    let cancelled = false;
    void document.fonts?.ready.then(() => {
      if (!cancelled) run();
    });

    return () => {
      cancelled = true;
      observer?.disconnect();
      delete el.dataset.equalizeGroup;
      el.style.minHeight = '';
      run();
    };
  }, [group, enabled]);
  return ref;
}
